import React, { memo, useEffect, useState } from 'react';
import styles from './_dealer-group-detail.module.scss';
import { GenericGrid } from '@amsanalytics/react-shared-library';
import { Dealer, DealerDealerGroupInfo, DealerReportInputStatus } from '../../../../Models/Dealer';
import { GridCustomCellProps, GridDetailRowProps, GridHeaderCellProps, GridRowProps } from '@progress/kendo-react-grid';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../Store/rootReducer';
import { DealerGroup } from '../../../../Models/DealerGroup';
import { ListView, ListViewItemProps } from '@progress/kendo-react-listview';
import { TextArea } from '@progress/kendo-react-inputs';
import { DealerReport } from '../../../../Models/DealerReport';
import { ExternalLinkCell, ExternalReportInputLinkCell, InternalReportLinkCell } from '../../../Controls/ExternalLink/ExternalLink';
import { DealerComposite } from '../../../../Models/DealerComposite';
import { formatMonthYear, getPreviousSixMonths, sortByYearThenMonthDescending } from '../../../../Helpers/Utilities';
import { includes, set } from 'ramda';
import { useNavigate } from 'react-router';
import DealerSelectorDialog from '../../../Dialogs/DealerDealerGroupEditorDialog/DealerDealerGroupEditorDialog';
import { useDispatch } from 'react-redux';
import { addDealerToDealerGroup, removeDealerFromDealerGroup, updateDealerDealerGroupMember } from '../../../../Store/Dealers/actions';
import DynamicButton from '../../../Controls/DynamicButton/DynamicButton';
import { useDialog } from '../../../Providers/DialogProvider';
import ConfirmationDialog from '../../../Dialogs/ConfirmationDialog/ConfirmationDialog';
import DealerDealerGroupMemberEditor from '../../../Dialogs/DealerDealerGroupMemberEditor/DealerDealerGroupMemberEditor';
import { updateClientPerformanceGroup } from '../../../../Store/Admin/actions';
import moment from 'moment';
import ReportViewerDialog from '../../../Dialogs/ReportViewerDialog/ReportViewerDialog';
import { authService } from '../../../..';
import CompositeReport from '../../../Controls/CompositeReport/CompositeReport';

// Create individual month status cells
export const MonthStatusCell = (props: GridCustomCellProps & { dealerGroup: DealerGroup, monthIndex: number }) => {
  const dataItem = props.dataItem as Dealer;
  const dealerGroup = props.dealerGroup;
  const monthIndex = props.monthIndex;
  
  const getStatusAbbreviation = (status: DealerReportInputStatus) => {
    switch (status) {
      case 'Not Started':
        return 'NS';
      case 'In Progress':
        return 'IP';
      case 'Completed':
        return 'C';
      default:
        return 'NS'
    }
  }
  
  const getStatusBackgroundColor = (status: DealerReportInputStatus) => {
    switch (status) {
      case 'Not Started':
        return '#ff6262';
      case 'In Progress':
        return 'yellow';
      case 'Completed':
        return '#7bc17b';
      default:
        return 'red'
    }
  }
  
  const getStatusColor = (status: DealerReportInputStatus) => {
    switch (status) {
      case 'Not Started':
      case 'In Progress':
        return 'black';
      default:
        return 'black'
    }
  }

  // Get the status for this specific month
  const getStatusForMonth = (): DealerReportInputStatus => {
    if (!dataItem?.reportInputStatusLastThreeMonths || !dealerGroup?.businessTypeIds) {
      return 'Not Started';
    }

    // Find the report status entry that matches any of the dealer group's business types
    const matchingReportStatus = dataItem.reportInputStatusLastThreeMonths.find(report =>
      dealerGroup.businessTypeIds.includes(report.businessTypeId)
    );

    if (matchingReportStatus && matchingReportStatus.monthlyStatuses.length > monthIndex) {
      return matchingReportStatus.monthlyStatuses[monthIndex];
    }

    return 'Not Started';
  };

  const status = getStatusForMonth();

  return (
    <td style={{ 
      cursor: 'pointer', 
      pointerEvents: 'all', 
      padding: '4px',
      width: '100%'
    }}>
      <div style={{
        backgroundColor: getStatusBackgroundColor(status),
        color: getStatusColor(status),
        textAlign: 'center',
        padding: '8px 0px',
        width: '100%',
        height: '100%'
      }}>
        {getStatusAbbreviation(status)}
      </div>
    </td>
  );
};

export const DealerActionCell = (props: GridCustomCellProps & {
  dealerGroupId: string, 
  setEditDealerDealerGroupInfo: (value: DealerDealerGroupInfo) => void
  }) => {
  const dispatch = useDispatch();
  const [openDialog, closeDialog] = useDialog();
  const dealer = props.dataItem as Dealer;
  return (
    <td style={{ cursor: `pointer`, pointerEvents: `all`, width: '100%' }}>
      <div style={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
        <DynamicButton
          style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%' }}
          onClick={() => {
            const dealerGroupInfo = dealer.dealerGroupLookupInfoList.find(dgi => dgi.dealerGroupId === props.dealerGroupId);
            if(dealerGroupInfo) {
              props.setEditDealerDealerGroupInfo({
                dealerId: props.dataItem.id,
                ...dealerGroupInfo
              });
            } else {
              alert("Dealer Group Info not found. This is an error. Please contact support.")
            }
          }}
          useIconButton={true}
          buttonContent={<span style={{ color: 'orange', fontSize: '20px' }} className="k-icon k-font-icon k-i-edit" />}
          color='yellow'
          title='Edit Dealer'
          
        />
         <DynamicButton
          style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%' }}
          onClick={() => {
            openDialog({
              children: (
                <ConfirmationDialog
                  title='Confirm Removal'
                  prompt={`Are you sure you want to remove this dealer from the group?`}
                  closeDialog={closeDialog}
                  accept={() => {
                    dispatch(removeDealerFromDealerGroup(props.dataItem.id, props.dealerGroupId));
                    closeDialog();
                  }}
                />
              ),
            });
          }}
          useIconButton={true}
          buttonContent={<span style={{ color: 'red', fontSize: '20px' }} className="k-icon k-font-icon k-i-delete" />}
          color='red'
          title='Delete Dealer'
          
        />
      </div>
    </td>
  );
}

const ModeratorsList = (props: ListViewItemProps) => {
  let item = props.dataItem;
  return (
    <div
      className="k-listview-item"
      style={{ padding: 10, borderBottom: '1px solid lightgrey' }}
    >
      <span>{item.firstName} {item.lastName}</span>
    </div>
  );
};

const CompositeCell = (props: GridCustomCellProps & { dealerGroup: string,  viewCompositeReport: (month: number, year: number, dealerGroup: string, reportTypeId: string) => void }) => {
  const dataItem = props.dataItem as DealerComposite;
  return (
    <td style={{ cursor: `pointer`, pointerEvents: `all`, width: '100%' }}>
      <div style={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
        <DynamicButton
          style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%' }}
          onClick={() => {
            props.viewCompositeReport(dataItem.month, dataItem.year, props.dealerGroup, dataItem.reportTypeId);
          }}
          useIconButton={true}
          buttonContent={<span style={{ color: 'green', fontSize: '20px' }} className="k-icon k-font-icon k-i-file-report" />}
          color='green'
          title='View Composite'
          
        />
      </div>
    </td>
  );
}

export default function DealerGroupDetail(props: GridDetailRowProps) {
  const allDealers = useSelector((state: AppState) => state.dealers.dealers);
  const allReports = useSelector((state: AppState) => state.performanceReport.dealerReports);
  const availableModerators = useSelector((state: AppState) => state.admin.moderators);
  const [selectedDealer, setSelectedDealer] = useState<Dealer>();
  const [editDealerDealerGroupInfo, setEditDealerDealerGroupInfo] = useState<DealerDealerGroupInfo>();
  const [dealerReportsForSelectedDealer, setDealerReportsForSelectedDealer] = useState<DealerReport[]>([]);  
  const dealerGroup: DealerGroup = props.dataItem;
  const [displayDealerDialog, setDisplayDealerDialog] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openDialog, closeDialog] = useDialog();
  const [bearerToken, setBearerToken] = useState<string>('');
  
  useEffect(() => {
    const setToken = async () => {
      const token = await authService.getToken();
      setBearerToken(token);
    }
    setToken()
      .catch(console.error);
  }, []);

  const viewCompositeReport = (month: number, year: number, performanceGroup: string, reportTypeId: string) => {
    openDialog({
      fullScreen: true,
      children: (
        <CompositeReport 
          bearerToken={bearerToken}
          year={year}
          month={month}
          reportTypeId={reportTypeId} 
          performanceGroup={performanceGroup}
          closeDialog={closeDialog}
          baseUrl={authService.appSettings.GetWebApiBaseUri()} 
        />
      ),
    });
  }

  // Get the dealers for the selected dealer group.
  const filteredDealers = allDealers.filter(d => includes(dealerGroup?.id?.toLowerCase(), d.dealerGroupLookupInfoList?.map(dgi => dgi.dealerGroupId.toLowerCase()) ?? [])).map(d => {
    if(selectedDealer?.id.toLowerCase() === d.id.toLowerCase()) {
      return {
        ...d,
        memberName: d.dealerGroupLookupInfoList.find(dgi => dgi.dealerGroupId === dealerGroup.id)?.memberName,
        memberNumber: d.dealerGroupLookupInfoList.find(dgi => dgi.dealerGroupId === dealerGroup.id)?.memberNumber,
        selected: true
      }
    }
    return {
      ...d,
      memberName: d.dealerGroupLookupInfoList.find(dgi => dgi.dealerGroupId?.toLowerCase() === dealerGroup.id?.toLowerCase())?.memberName ?? 'ABC',
      memberNumber: d.dealerGroupLookupInfoList.find(dgi => dgi.dealerGroupId?.toLowerCase() === dealerGroup.id?.toLowerCase())?.memberNumber ?? '123',
      selected: false
    }
  });

  // Get the earliest month and year
  const dealerGroupReports = allReports.filter(r => includes(r.clientId, filteredDealers.map(fd => fd.id)));
  
  const earliestMonth = Math.min(...dealerGroupReports.map(report => report.month));
  const earliestYear = Math.min(...dealerGroupReports.map(report => report.year));
  
  const composites: DealerComposite[] = [];
  
  for (let year = earliestYear; year <= moment().year(); year++) {
      for (let month = (year === earliestYear ? earliestMonth : 1); month <= (year === moment().year() ? moment().month() : 12); month++) {
          let reports = dealerGroupReports.filter(report => report.year === year && report.month === month);
          let status: 'Pending' | 'Ready' = 'Ready';
          if (reports.length === 0 || reports.some(report => report.status !== 'Submitted')) {
            status = 'Pending';
          }          
          composites.push({month, year, status, reportTypeId: dealerGroup.businessTypeIds?.[0]});
      }
  }
  const dealerGroupComposites: DealerComposite[]  = composites.sort(sortByYearThenMonthDescending);

  // Get the month names for the headers
  const lastSixMonths = getPreviousSixMonths();

  return (
    <>
    <div className={styles.dealerGroupDetailContainer}>
      <div style={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
        <GenericGrid<Dealer>
          style={{ display: 'flex', flex: 1, height: '400px' }}
          items={filteredDealers ?? []}
          itemName='Dealers'
          itemNameStyle={{ color: '#d95137' }}
          disableEditing={true}
          disableAdding={false}
          useIconButtons={true}
          onOpenItemEditor={() => { 
            setDisplayDealerDialog(true);
          }}
          disableGrouping={true}
          selectedField={'selected'}
          onItemSelected={(dealer: Dealer) => {
            if (dealer) {
              setSelectedDealer(dealer);
              setDealerReportsForSelectedDealer(allReports.filter(dr => dr.clientId === dealer.id));
            }
          }}
          sortDescriptors={[{ field: 'name', dir: 'asc' }]}
          size="small"
          columns={[
            { width: '70px', preventFiltering: true, preventSorting: true,
              cell: (props: GridCustomCellProps) => <DealerActionCell {...props} dealerGroupId={dealerGroup.id!} setEditDealerDealerGroupInfo={setEditDealerDealerGroupInfo} /> },
            { field: "name", width: "700px", title: "Dealer", sortable: true }, 
            { field: "memberName", width: "150px", title: "Member", preventFiltering: true }, 
            { field: "memberNumber", width: "150px", title: "Member #", preventFiltering: true },
            // Individual columns for each month
            ...lastSixMonths.map((month, index) => ({
              field: `month${index}`,
              width: "100px",
              title: month,
              preventFiltering: true,
              preventSorting: true,
              cell: (props: GridCustomCellProps) => <MonthStatusCell {...props} dealerGroup={dealerGroup} monthIndex={index} />
            }))
          ]}
        />
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            padding: '10px',
            marginRight: '5px',
            maxWidth: '335px'
          }}
        >
          <div style={{ marginTop: '10px' }}>Moderators</div>
          <ListView
            style={{ border: 'solid 1px rgba(0, 0, 0, 0.08)', width: '100%' }}
            data={availableModerators.filter(am => includes(am.id, dealerGroup.moderatorIds))}
            item={ModeratorsList}
          />
        </div>
      </div>
      <div style={{ display: 'flex', flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, width: '50%' }}>
          <GenericGrid<DealerComposite>
            disableEditing={true}
            disableAdding={true}
            disableGrouping={true}
            itemName='Dealer Group Composites'
            style={{ color: '#d95137', textWrap: 'nowrap' }}
            items={dealerGroupComposites.map((dc) => {
              return {
                ...dc,
                period: formatMonthYear(dc.month, dc.year)
              };
            })}
            selectedField={'selected'}
            onItemSelected={(item: DealerComposite) => {
              if (item) {

              }
            }}
            size="small"
            columns={[            
              { field: "period", title: "Period", width: '100px', preventFiltering: true, sortable: true },
              { field: "status", title: "Status" },
              { field: '', title: ' ', preventFiltering: true, preventSorting: true, cell: (props: any) => <CompositeCell {...props} viewCompositeReport={viewCompositeReport} dealerGroup={dealerGroup.name} />, width: '50px' }
            ]}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <GenericGrid<DealerReport>
            disableEditing={true}
            disableAdding={true}
            disableGrouping={true}
            itemName='Dealer Inputs'
            itemNameStyle={{ color: '#d95137' }}
            style={{ display: 'flex', flex: 1, height: '400px' }}
            items={dealerReportsForSelectedDealer?.map((dr) => {
              return {
                ...dr,
                name: selectedDealer?.name,
                date: new Date(dr.date),
                period: formatMonthYear(dr.month, dr.year), 
              };
            }) ?? []}
            selectedField={'selected'}
            onItemSelected={(item: DealerReport) => {
              if (item) {

              }
            }}
            size="small"
            columns={[
              { field: '', title: ' ', cell: (props: any) => <InternalReportLinkCell {...props} 
                icon={<span style={{color: '#d95137', fontSize: '25px', cursor: 'pointer'}} 
                className="k-icon k-font-icon k-i-track-changes" />}
                onReportClick={(dealerReport: DealerReport) => {
                  navigate(`/performanceReporting/${dealerReport.id}`)
                }} />, 
                width: '50px', preventFiltering: true
              },
              { field: "period", title: "Period", width: '100px', preventFiltering: true },
              { field: "status", title: "Status" },
              { field: "date", width: "90px", title: "Date", format: "{0:d}", preventFiltering: true },
            ]}
          />
        </div>
      </div>
    </div>
    {displayDealerDialog && dealerGroup.id && (
     <DealerSelectorDialog
      dealers={allDealers.filter(d => !includes(d.id, filteredDealers.map(fd => fd.id)))}
      onCancel={() => setDisplayDealerDialog(false)}
      dealerGroupId={dealerGroup.id}
      onSubmit={(dealerDealerGroup: DealerDealerGroupInfo) => {
        dispatch(addDealerToDealerGroup(dealerDealerGroup));
        setDisplayDealerDialog(false)
      }}
     />
    )}
    {editDealerDealerGroupInfo && (
      <DealerDealerGroupMemberEditor
        dealerDealerGroupInfo={editDealerDealerGroupInfo}
        onCancel={() => setEditDealerDealerGroupInfo(undefined)}
        onSubmit={(dealerDealerGroupInfo: DealerDealerGroupInfo) => {
          dispatch(updateDealerDealerGroupMember(dealerDealerGroupInfo));
          setEditDealerDealerGroupInfo(undefined);
        }}
      />
    )}
    </>
  );
}